import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AdminPagination from "../../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../../component/SkeletonLoading";
import { useGetParentCancellationsQuery } from "../../../../store/api/school/ReservationSlice";
import { formatDateToLocal } from "../../../../utils/dateAndTimeConversion";

const formatText = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const ParentCancellationsList = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [bookingTypeFilter, setBookingTypeFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);

  const { data, isLoading, isFetching } = useGetParentCancellationsQuery({
    page,
    limit,
    search: search || undefined,
    booking_type: bookingTypeFilter || undefined,
    role_filter: role === "super_admin" ? roleFilter || undefined : undefined,
  });

  const handleBookingTypeFilter = (nextType) => {
    setBookingTypeFilter(nextType);
    setPage(1);
  };

  const handleRoleFilter = (nextRole) => {
    setRoleFilter(nextRole);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const cancellations = useMemo(() => data?.data?.cancellations || [], [data]);
  const pagination = data?.data?.pagination || {
    page,
    limit,
    total: 0,
    totalPages: 0,
  };

  const handlePageChange = (nextPage) => setPage(nextPage);
  const handleLimitChange = (nextLimit) => {
    setLimit(nextLimit);
    setPage(1);
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between">
        <div className="mb-2 total">
          {t("total")} {t("parent")} {t("cancellations")}:{" "}
          {pagination?.total ?? 0}
        </div>
        <div className="mb-2 d-flex flex-wrap gap-2">
          <div style={{ minWidth: 220 }}>
            <input
              type="text"
              className="form-control"
              placeholder={`${t("search")} ${t("student")}/${t("parent")}...`}
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          {role === "super_admin" && (
            <div style={{ minWidth: 220 }}>
              <select
                className="form-select"
                value={roleFilter || ""}
                onChange={(e) => handleRoleFilter(e.target.value || null)}
              >
                <option value="">
                  {t("all")} {t("reservations")}
                </option>
                <option value="school">
                  {t("schools")} {t("reservation")}
                </option>
                <option value="vendor">
                  {t("vendors")} {t("reservation")}
                </option>
                <option value="transfer_to_vendor">
                  {t("transfer_to")} {t("vendors")}
                </option>
              </select>
            </div>
          )}
          {/* <div style={{ minWidth: 220 }}>
            <select
              className="form-select"
              value={bookingTypeFilter || ""}
              onChange={(e) =>
                handleBookingTypeFilter(e.target.value || null)
              }
            >
              <option value="">
                {t("all")} {t("types")}
              </option>
              <option value="busing_subscription">
                {t("busing")} {t("subscription")}
              </option>
              <option value="bus_charter">
                {t("bus")} {t("charter")}
              </option>
            </select>
          </div> */}
        </div>
      </div>

      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th className="text-nowrap">
                {t("Sr")}.{t("no")}.
              </th>
              <th className="text-nowrap">{t("date")}</th>
              <th className="text-nowrap">{t("student")}</th>
              <th className="text-nowrap">{t("parent")}</th>
              <th className="text-nowrap">
                {t("booking")} {t("type")}
              </th>
              {role === "super_admin" && (
                <th className="text-nowrap">{t("owner")}</th>
              )}
              <th className="text-nowrap">{t("reason")}</th>
              <th className="text-nowrap">
                {t("cancelled")} {t("at")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading
                MainLength={10}
                length={role === "super_admin" ? 8 : 7}
              />
            ) : cancellations.length > 0 ? (
              cancellations.map((item, i) => (
                <tr key={item.cancellation_id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.instance_date)}
                  </td>
                  <td className="text-nowrap">
                    <div>{item.student_name || "N/A"}</div>
                    {item.roll_number && (
                      <div className="text-muted small">
                        #{item.roll_number}
                      </div>
                    )}
                  </td>
                  <td className="text-nowrap">
                    <div>{item.parent_name || "N/A"}</div>
                    <div className="text-muted small">
                      {item.parent_email || ""}
                    </div>
                  </td>
                  <td className="text-nowrap">
                    {formatText(item.booking_type)}
                  </td>
                  {role === "super_admin" && (
                    <td className="text-nowrap">
                      {item.school_name || item.vendor_name || t("unassigned")}
                    </td>
                  )}
                  <td style={{ maxWidth: 260 }}>
                    {item.cancellation_reason || (
                      <span className="text-muted">
                        {t("no")} {t("reason")} {t("provided")}
                      </span>
                    )}
                  </td>
                  <td className="text-nowrap">
                    {formatDateTime(item.cancelled_at)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={role === "super_admin" ? 8 : 7}
                  className="text-center"
                >
                  {t("no")} {t("parent")} {t("cancellations")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
    </>
  );
};

export default ParentCancellationsList;
